// ABOUTME: Page listing the user's square packs with create functionality.
// ABOUTME: Fetches packs on mount; opens a dialog to create a new pack.
import { useState, useEffect } from 'react'
import { useAuth } from '@clerk/react'
import { useNavigate } from 'react-router'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import IosShareIcon from '@mui/icons-material/IosShare'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { FormCard, FormCardHeader, FormCardBody, FormCardFooter } from '../theme'
import {
  squarePacksControllerList,
  squarePacksControllerCreate,
  squarePacksControllerGenerateSquareSuggestions,
  squarePacksControllerAddSquare,
  type SquarePackDto,
} from '../api/generated'
import { Pagination } from '../components/Pagination'
import { ShareLinkDialog } from '../components/ShareLinkDialog'
import { SquarePackRowSkeleton } from '../components/SquarePackRowSkeleton'
import { CreateSquarePackDialog } from '../components/CreateSquarePackDialog'
import { trackShareLinkCopied } from '../analytics/events'

export function SquarePacksPage() {
  const { getToken } = useAuth()
  const navigate = useNavigate()
  const [packs, setPacks] = useState<SquarePackDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [sharePackId, setSharePackId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    async function fetch() {
      setIsLoading(true)
      const token = await getToken()
      try {
        const res = await squarePacksControllerList(
          { page, limit: 50 },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        setPacks(res.data.items as unknown as SquarePackDto[])
        setTotalPages(res.data.totalPages)
      } finally {
        setIsLoading(false)
      }
    }
    fetch()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  async function handleCreatePack(title: string, randomCalled: boolean) {
    const token = await getToken()
    const res = await squarePacksControllerCreate(
      { title, randomCalled },
      { headers: { Authorization: `Bearer ${token}` } },
    )
    return res.data
  }

  async function handleGenerateSquares(packId: string, topic: string) {
    const token = await getToken()
    const res = await squarePacksControllerGenerateSquareSuggestions(
      packId,
      { topic },
      { headers: { Authorization: `Bearer ${token}` } },
    )
    return res.data.suggestions
  }

  async function handleAddSquares(packId: string, titles: string[]) {
    const token = await getToken()
    await Promise.all(
      titles.map((title) =>
        squarePacksControllerAddSquare(packId, { title }, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ),
    )
  }

  function handlePackCreated(packId: string) {
    navigate(`/square-packs/${packId}`)
  }

  function shareUrlFor(packId: string) {
    return `${window.location.origin}/games/new?squarePackId=${packId}`
  }

  const ownedPacks = packs.filter((c) => c.ownerId !== null)

  return (
    <Box pt={4}>
      <FormCard>
        <FormCardHeader>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="h4" component="h1">Square Packs</Typography>
            <Button color="inherit" variant="outlined" onClick={() => setDialogOpen(true)}>New Square Pack</Button>
          </Stack>
        </FormCardHeader>
        <FormCardBody>
          {isLoading ? (
            <List disablePadding>
              {[0, 1, 2].map((i) => (
                <SquarePackRowSkeleton key={i} />
              ))}
            </List>
          ) : ownedPacks.length === 0 ? (
            <Typography color="text.secondary">No square packs yet. Create one to get started.</Typography>
          ) : (
            <List disablePadding>
              {ownedPacks.map((c) => (
                <ListItem
                  key={c.id}
                  disablePadding
                  secondaryAction={
                    c.shared ? (
                      <Tooltip title="Share pack link">
                        <IconButton
                          edge="end"
                          aria-label={`share ${c.title}`}
                          onClick={(e) => { e.stopPropagation(); setSharePackId(c.id) }}
                        >
                          <IosShareIcon />
                        </IconButton>
                      </Tooltip>
                    ) : undefined
                  }
                >
                  <ListItemButton onClick={() => navigate(`/square-packs/${c.id}`)}>
                    <ListItemText primary={c.title} />
                    <ChevronRightIcon color="action" />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </FormCardBody>
        <FormCardFooter>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </FormCardFooter>
      </FormCard>

      {sharePackId && (
        <ShareLinkDialog
          open={sharePackId !== null}
          onClose={() => setSharePackId(null)}
          url={shareUrlFor(sharePackId)}
          title="Share Square Pack"
          description="Anyone with this link can create a game using this pack."
          onCopy={() => trackShareLinkCopied('square_pack', sharePackId)}
        />
      )}

      <CreateSquarePackDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={handlePackCreated}
        onCreate={handleCreatePack}
        onGenerate={handleGenerateSquares}
        onAddSquares={handleAddSquares}
      />
    </Box>
  )
}
